import { useState, useEffect, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { FileApi } from '../../api/file/file-api';
import moment from 'moment-timezone';
import InventoryGallery from './inventory-gallery';
import { generateObjectId } from '../../utils/idUtils';

export const InventoryModal = ({ show, cancel, tenancy, tenancyIndex, tenant, setTenant, inventory, onSave, asPage = false, uploadProgress = [], savingInProgress = false }) => {

    // Map various legacy/string type values to the server enum integers
    const mapTypeToEnum = (t) => {
        if (typeof t === 'number') return t;
        if (!t) return 2; // default Periodic
        const s = String(t).toLowerCase();
        if (s === 'checkin' || s === 'check-in' || s === 'move-in' || s === 'move_in' || s === 'move in') return 0; // CheckIn
        if (s === 'checkout' || s === 'check-out' || s === 'move-out' || s === 'move_out' || s === 'move out') return 1; // CheckOut
        return 2; // Periodic / default
    };

    const isEdit = !!inventory;

    const [type, setType] = useState(mapTypeToEnum(inventory?.type));
    const [date, setDate] = useState(inventory?.date ? moment.utc(inventory.date).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'));
    const [notes, setNotes] = useState(inventory?.notes ?? '');
    const [photos, setPhotos] = useState(inventory?.photos ? inventory.photos.map(p => ({ ...p, id: p.id ?? generateObjectId() })) : []);

    useEffect(() => {
        if (inventory) {
            setType(mapTypeToEnum(inventory.type));
            setDate(inventory.date ? moment.utc(inventory.date).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'));
            setNotes(inventory.notes ?? '');
            // Ensure each photo has a client-side id for reliable UI operations (remove/update)
            setPhotos(inventory.photos ? inventory.photos.map(p => ({ ...p, id: p.id ?? generateObjectId() })) : []);
        } else {
            setType(2);
            setDate(moment().format('YYYY-MM-DD'));
            setNotes('');
            setPhotos([]);
        }
    }, [inventory, show]);


    const removePhoto = (id) => setPhotos(prev => prev.filter(p => p.id !== id));

    const updatePhotoComment = (id, comment) => setPhotos(prev => prev.map(p => p.id === id ? { ...p, comment } : p));

    const { instance, inProgress, accounts } = useMsal();
    const localFileApi = useMemo(() => new FileApi(instance, inProgress, accounts, false), [instance, inProgress, accounts]);
    const [localUploadProgress, setLocalUploadProgress] = useState([]);

    const effectiveUploadProgress = (uploadProgress && uploadProgress.length > 0) ? uploadProgress : localUploadProgress;
    const isUploading = Array.isArray(effectiveUploadProgress) && effectiveUploadProgress.length > 0 && effectiveUploadProgress.some(p => p.remainingBytes > 0);

    if (!show) return null;

    const renderProgress = () => {
        // If there are active file upload progress entries, show them
        if (effectiveUploadProgress && effectiveUploadProgress.length > 0) {
            return (
                <div className="mt-2">
                    <label className="form-label">Upload progress</label>
                    {effectiveUploadProgress.map(p => {
                        const original = p.originalSize || 0;
                        const remaining = p.remainingBytes || 0;
                        const percent = original > 0 ? Math.max(0, Math.min(100, Math.round(((original - remaining) / original) * 100))) : 0;
                        const humanRemaining = remaining >= 1024 ? `${Math.round(remaining/1024)} KB` : `${remaining} B`;
                        return (
                            <div key={p.name} className="mb-1">
                                <div className="d-flex justify-content-between small"><div>{p.name}</div><div>{humanRemaining}</div></div>
                                <div className="progress" style={{ height: 8 }}>
                                    <div className="progress-bar" role="progressbar" style={{ width: `${percent}%` }} aria-valuenow={percent} aria-valuemin={0} aria-valuemax={100}></div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            );
        }

        // If uploads finished but the tenant save/update is still in progress,
        // show a small spinner so the user knows something is still happening.
        if (savingInProgress) {
            return (
                <div className="mt-2 d-flex align-items-center">
                    <div className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></div>
                    <small className="text-muted">Saving inventory...</small>
                </div>
            );
        }

        return null;
    };

    const save = async () => {
        // ensure tenancy has inventories; find tenancy by id rather than by reference
        const existingTenancies = tenant.tenancies || [];
        // Try to find tenancy by id first. If tenancy has no id or lookup fails,
        // fall back to object identity. If that also fails and tenancyIndex is
        // provided, use that index.
        let tIndex = -1;

        // If not found by server id, try client-side id assigned by TenantApi
        if (tenancy && tenancy.clientId) {
            tIndex = existingTenancies.findIndex(t => t && t.clientId === tenancy.clientId);
        }

        // Object identity fallback (very brittle) — keeps backward compatibility
        if (tIndex === -1 && tenancy) {
            tIndex = existingTenancies.findIndex(t => t === tenancy);
        }

        // Last resort: use explicit tenancyIndex passed in
        if (tIndex === -1 && typeof tenancyIndex === 'number') {
            if (tenancyIndex >= 0 && tenancyIndex < existingTenancies.length) tIndex = tenancyIndex;
        }
        if (tIndex === -1) {
            console.error('InventoryModal.save: tenancy not found on tenant');
            return cancel();
        }

        const newInventory = {
            id: inventory?.id ?? generateObjectId(),
            type,
            date: date ? new Date(date).toISOString() : new Date().toISOString(),
            notes,
            photos: photos.map(p => ({
                id: p.id ?? generateObjectId(),
                // Do not persist original file name; title should be entered by user
                title: p.title ?? '',
                path: p.path ?? null,
                thumbPath: p.thumbPath ?? null,
                // Do not include client-only SAS token fields here; server will provide
                // any SAS-tokened preview URLs when the file is fetched.
                dataUrl: p.dataUrl ?? null,
                // Preserve original File object so caller can upload it before persisting
                file: p.file,
                comment: p.comment ?? ''
            }))
        };

        const updatedTenancies = [...existingTenancies];
        const tenancyCopy = { ...updatedTenancies[tIndex] };
        tenancyCopy.inventories = tenancyCopy.inventories ? [...tenancyCopy.inventories] : [];

        if (isEdit) {
            const invIdx = tenancyCopy.inventories.findIndex(inv => inv.id === newInventory.id);
            if (invIdx >= 0) tenancyCopy.inventories[invIdx] = newInventory;
            else tenancyCopy.inventories.push(newInventory);
        } else {
            tenancyCopy.inventories.push(newInventory);
        }

        updatedTenancies[tIndex] = tenancyCopy;

        const updatedTenant = { ...tenant, tenancies: updatedTenancies };

        // If the tenant has an activeTenancy that refers to the tenancy we just
        // modified, update activeTenancy to the new tenancy object so their IDs
        // and contents remain in sync in the UI.
        try {
            const currentActive = tenant?.activeTenancy;
            if (currentActive) {
                const activeMatches = (currentActive.id && currentActive.id === tenancyCopy.id)
                    || (currentActive.clientId && currentActive.clientId === tenancyCopy.clientId)
                    || (currentActive === tenancy);

                if (activeMatches) {
                    updatedTenant.activeTenancy = tenancyCopy;
                }
            }
        } catch (e) {
            // non-fatal; if anything goes wrong, leave activeTenancy unchanged
        }

        // If this modal is being shown as a page, the page-level wrapper (InventoryDetail)
        // will handle uploads. If it's a modal, do the upload here so the caller doesn't
        // need to handle file uploads.
        if (!asPage) {
            try {
                const companyName = updatedTenant?.company?.name || tenant?.company?.name;
                if (companyName && newInventory && Array.isArray(newInventory.photos)) {
                    const photosWithFiles = newInventory.photos.filter(p => p && p.file);
                    if (photosWithFiles.length > 0) {
                        const collection = photosWithFiles.map(p => p.file);
                        const pathBase = `${updatedTenant.id || tenant.id}/Inventories/${newInventory.id}`;
                        setLocalUploadProgress(collection.map(c => ({ name: c.name, remainingBytes: c.size, originalSize: c.size })));
                        const uploadResponses = await localFileApi.uploadFiles(companyName, pathBase, collection, (details) => {
                            const withOriginal = (details || []).map(d => ({ originalSize: d.originalSize || (d.remainingBytes || 0), name: d.name, remainingBytes: d.remainingBytes }));
                            setLocalUploadProgress(withOriginal);
                        });

                        for (const resp of uploadResponses) {
                            try {
                                const fileName = resp.file && resp.file.name;
                                for (const photo of newInventory.photos || []) {
                                    if (photo.file && photo.file.name === fileName) {
                                        photo.path = resp.path || photo.path;
                                        if (resp.pathIncSASToken) photo.pathIncSASToken = resp.pathIncSASToken;
                                        if (resp.thumbPathIncSASToken) photo.thumbPathIncSASToken = resp.thumbPathIncSASToken;
                                    }
                                }
                            } catch (e) {
                                console.warn('InventoryModal: Error mapping uploaded file response', e);
                            }
                        }

                        // Remove file objects before persisting
                        for (const photo of newInventory.photos || []) {
                            if (photo.file) delete photo.file;
                        }

                        setLocalUploadProgress([]);
                    }
                }
            } catch (uploadErr) {
                console.error('InventoryModal: Error uploading photos', uploadErr);
                // proceed to call onSave so tenant changes are not lost
            }
        }

        if (typeof onSave === 'function') {
            // Delegate persistence to caller. Provide the created/updated inventory as a
            // second argument so the caller doesn't need to diff state to find it.
            onSave(updatedTenant, newInventory);
        } else if (typeof setTenant === 'function') {
            setTenant(updatedTenant);
        }

        // Only close the modal/page if this is a modal instance. When rendered
        // as a page (`asPage === true`) the parent page wrapper handles navigation
        // after uploads/persistence complete, so don't call cancel() here.
        if (!asPage) cancel();
    };

    if (asPage) {
        // Render full page style with two panels to match the Settings page look-and-feel.
        return (
            <div className="p-3">
                <h5 className="mb-2">{isEdit ? 'Edit' : 'Add'} Inventory</h5>
                <div className="row">
                    <div className="col-12 col-md-6">
                        <div className="panel p-3 rounded">
                            <div className="mb-2">
                                <label className="form-label">Type</label>
                                <select className="form-select" value={type} onChange={(e) => setType(parseInt(e.target.value, 10))}>
                                    <option value={0}>Move In</option>
                                    <option value={1}>Move Out</option>
                                    <option value={2}>Periodic</option>
                                </select>
                            </div>

                            <div className="mb-2">
                                <label className="form-label">Date</label>
                                <input className="form-control" type="date" value={date} onChange={e => setDate(e.target.value)} />
                            </div>

                            <div className="mb-3">
                                <label className="form-label">Inventory notes</label>
                                <textarea className="form-control" rows={6} style={{ minHeight: '250px' }} value={notes} onChange={e => setNotes(e.target.value)} />
                            </div>

                            <div className="d-flex justify-content-start mt-3">
                                <button type="button" className="btn btn-secondary me-2" onClick={cancel} disabled={isUploading}>Cancel</button>
                                <button type="button" className="btn btn-primary" onClick={save} disabled={isUploading}>{isUploading ? 'Uploading...' : 'Save Inventory'}</button>
                            </div>
                            {renderProgress()}
                        </div>
                    </div>

                    <div className="col-12 col-md-6">
                        <div className="panel p-3 rounded">
                            <div className="mb-2">
                                <label className="form-label">Photos</label>
                                <InventoryGallery
                                    uploadContainerStyle={{ maxWidth: 600 }}
                                    photos={photos}
                                    onFilesSelected={async (files) => {
                                        const list = Array.from(files || []);
                                        const readers = await Promise.all(list.map(file => new Promise((res) => {
                                                const reader = new FileReader();
                                                reader.onload = e => res({
                                                    id: generateObjectId(),
                                                    fileName: null,
                                                    title: '',
                                                    path: null,
                                                    thumbPath: null,
                                                    pathIncSASToken: null,
                                                    thumbPathIncSASToken: null,
                                                    dataUrl: e.target.result,
                                                    file: file,
                                                    comment: ''
                                                });
                                                reader.readAsDataURL(file);
                                            })));
                                        setPhotos(prev => [...prev, ...readers]);
                                    }}
                                    onRemovePhoto={(id) => removePhoto(id)}
                                    onUpdatePhotoComment={(id, comment) => updatePhotoComment(id, comment)}
                                    onUpdatePhotoTitle={(id, title) => setPhotos(prev => prev.map(p => p.id === id ? { ...p, title } : p))}
                                />
                            </div>

                            {/* upload progress moved under the Save button */}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.85)', zIndex: 2000, overflowY: 'auto' }} aria-modal="true">
            <div className="container py-4">
                <div className="modal-dialog modal-lg mx-auto" style={{ maxWidth: 900 }}>
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">{isEdit ? 'Edit' : 'Add'} Inventory</h5>
                            <button type="button" className="btn-close" aria-label="Close" onClick={cancel}></button>
                        </div>
                        <div className="modal-body">
                            <div className="mb-2">
                                <label className="form-label">Type</label>
                                <select className="form-select" value={type} onChange={(e) => setType(parseInt(e.target.value, 10))}>
                                    <option value={0}>Move In</option>
                                    <option value={1}>Move Out</option>
                                    <option value={2}>Periodic</option>
                                </select>
                            </div>

                            <div className="mb-2">
                                <label className="form-label">Date</label>
                                <input className="form-control" type="date" value={date} onChange={e => setDate(e.target.value)} />
                            </div>

                            <div className="mb-3">
                                <label className="form-label">Inventory notes</label>
                                <textarea className="form-control" rows={3} value={notes} onChange={e => setNotes(e.target.value)} />
                            </div>

                            <div className="mb-2">
                                    <label className="form-label">Photos</label>
                                    <InventoryGallery
                                        uploadContainerStyle={{ maxWidth: 600 }}
                                        photos={photos}
                                        onFilesSelected={async (files) => {
                                        const list = Array.from(files || []);
                                        const readers = await Promise.all(list.map(file => new Promise((res) => {
                        const reader = new FileReader();
                        reader.onload = e => res({
                            id: generateObjectId(),
                            fileName: null,
                            title: '',
                            path: null,
                            thumbPath: null,
                            pathIncSASToken: null,
                            thumbPathIncSASToken: null,
                            dataUrl: e.target.result,
                            file: file,
                            comment: ''
                        });
                        reader.readAsDataURL(file);
                        })));
                                        setPhotos(prev => [...prev, ...readers]);
                                    }}
                                    onRemovePhoto={(id) => removePhoto(id)}
                                    onUpdatePhotoComment={(id, comment) => updatePhotoComment(id, comment)}
                                />
                            </div>
                        </div>
                        <div className="modal-footer">
                            <div className="w-100">
                                {renderProgress()}
                            </div>
                            <button type="button" className="btn btn-secondary" onClick={cancel} disabled={isUploading}>{isUploading ? 'Uploading...' : 'Cancel'}</button>
                            <button type="button" className="btn btn-primary" onClick={save} disabled={isUploading}>{isUploading ? 'Uploading...' : 'Save Inventory'}</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

};

export default InventoryModal;
